import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import jwtDecode from 'jwt-decode';
import APIHelper from '../utils/helpers/APIHelper';
import APIParamsBuilderHelper from '../utils/helpers/APIParamsBuilderHelper';
import { AuthConstants } from '../utils/constants/AuthConstants';
import { JourneyConstants } from '../utils/constants/JourneyConstants';
import { HttpMethods } from '../utils/constants/HttpMethodsConstants';
import BrowserHelper from '../utils/helpers/BrowserHelper';

export interface Session {
  access_token: string;
  expires_in: number;
  refresh_token: string;
  refresh_expires_in: number;
  token_type: string;
  session_state: string;
}

export interface SessionModel {
  email: string;
  firstName: string;
  lastName: string;
}

export interface User {
  email?: string;
  name?: string;
  given_name?: string;
  family_name?: string;
  phoneNumber?: string;
  state?: string;
  specialty?: string;
  npiNumber?: string;
}

interface InsuredSession {
  indicationNo?: string;
  applicationNo?: string;
  applicationStatus?: string;
  indSessionId?: string;
  appSessionId?: string;
  indicationId?: number;
  applicationId?: number;
  portalUserId?: number;
}

/**
 * @method setToken
 * @return set user session from token to sessionStorage
 */
export function setToken(token: string): void {
  const user = jwtDecode(token) as User;

  BrowserHelper.setSessionStorageItem(AuthConstants.USER_TOKEN, token);
  BrowserHelper.setSessionStorageItem(AuthConstants.USER_DATA_EMAIL, user.email || '');
  BrowserHelper.setSessionStorageItem(AuthConstants.USER_DATA_FIRSTNAME, user.given_name || '');
  BrowserHelper.setSessionStorageItem(AuthConstants.USER_DATA_LASTNAME, user.family_name || '');
}

/**
 * @method setSession
 * @return set user app session to sessionStorage
 */
export function setSession(data: InsuredSession): void {
  BrowserHelper.setSessionStorageItem(JourneyConstants.IND_SESSION_ID, data?.indSessionId);
  BrowserHelper.setSessionStorageItem(JourneyConstants.APP_SESSION_ID, data?.appSessionId);
  BrowserHelper.setSessionStorageItem(JourneyConstants.INDICATION_ID, `${data?.indicationId}`);
  BrowserHelper.setSessionStorageItem(JourneyConstants.APPLICATION_ID, `${data?.applicationId}`);
  BrowserHelper.setSessionStorageItem(JourneyConstants.PORTAL_USER_ID, `${data?.portalUserId}`);
  BrowserHelper.setSessionStorageItem(JourneyConstants.APPLICATION_STATUS, `${data?.applicationStatus}`);
}

/**
 * @method getLocalUser
 * @return user object from sessionStorage
 */
export function getLocalUser(): User {
  const token = BrowserHelper.getSessionStorageItem(AuthConstants.USER_TOKEN);

  if (!token) {
    throw new Error('Could not retrieve token');
  }

  const decodedToken = jwtDecode(token);
  return decodedToken as User;
}

/**
 * @method getUser
 * @return user object from sessionStorage
 */
export function getUser(): User {
  return getLocalUser();
}

/**
 * @method getToken
 * @return user token from sessionStorage
 */
export function getToken(): string {
  const token = BrowserHelper.getSessionStorageItem(AuthConstants.USER_TOKEN) || '';
  return token;
}

/**
 * @method isLoggedIn
 * @return true or false flag to indicate user logged in or not
 */
export const isLoggedIn = (): boolean => {
  const token = BrowserHelper.getSessionStorageItem(AuthConstants.USER_TOKEN);
  return !!token;
};

/**
 * @method logout
 * @return void
 * @description remove user session and app session from sessionStorage
 */
export function logout(): void {
  BrowserHelper.deleteSessionStorageItem(AuthConstants.USER_TOKEN);
  BrowserHelper.deleteSessionStorageItem(AuthConstants.USER_DATA_EMAIL);
  BrowserHelper.deleteSessionStorageItem(AuthConstants.USER_DATA_FIRSTNAME);
  BrowserHelper.deleteSessionStorageItem(AuthConstants.USER_DATA_LASTNAME);

  BrowserHelper.deleteSessionStorageItem(JourneyConstants.IND_SESSION_ID);
  BrowserHelper.deleteSessionStorageItem(JourneyConstants.APP_SESSION_ID);
  BrowserHelper.deleteSessionStorageItem(JourneyConstants.INDICATION_ID);
  BrowserHelper.deleteSessionStorageItem(JourneyConstants.APPLICATION_ID);
  BrowserHelper.deleteSessionStorageItem(JourneyConstants.PORTAL_USER_ID);
}

export function setBaseUrl(url: string): void {
  axios.defaults.baseURL = url;
}

export function addRequestInterceptors(
  successCb?: (res: AxiosRequestConfig) => AxiosRequestConfig | Promise<AxiosRequestConfig>,
  errorCb?: (err: AxiosError) => Promise<AxiosError>,
): number {
  // Attach JWT token to all outgoing AJAX requests
  return axios.interceptors.request.use(successCb, errorCb);
}

export function removeRequestInterceptors(id: number): void {
  axios.interceptors.request.eject(id);
}

export function addResponseInterceptors<T>(
  successCb?: (res: AxiosResponse<T>) => AxiosResponse<T> | Promise<AxiosResponse<T>>,
  errorCb?: (err: AxiosError) => Promise<AxiosError>,
): number {
  return axios.interceptors.response.use(successCb, errorCb);
}

export function removeResponseInterceptors(id: number): void {
  axios.interceptors.response.eject(id);
}

setBaseUrl(`${process.env.GATSBY_DYNAMIC_API_URL}${process.env.GATSBY_DYNAMIC_API_VERSION}`);

addRequestInterceptors(
  // Attach JWT token to header and add user query param to all outgoing requests
  (config) => {
    const isQueryParam =
      config.method?.toLocaleLowerCase() === HttpMethods.GET.toLocaleLowerCase() ||
      config.method?.toLocaleLowerCase() === HttpMethods.DELETE.toLocaleLowerCase();
    const isBodyParam =
      config.method?.toLocaleLowerCase() === HttpMethods.POST.toLocaleLowerCase() ||
      config.method?.toLocaleLowerCase() === HttpMethods.PUT.toLocaleLowerCase();
    const userParam = getUser()?.email;

    return {
      ...config,
      headers: {
        ...config.headers,
        Authorization: `Bearer ${BrowserHelper.getSessionStorageItem(AuthConstants.USER_TOKEN)}`,
      },
      params: {
        ...config.params,
        user: isQueryParam ? userParam : '',
      },
      data: {
        ...config.data,
        user: isBodyParam ? userParam : '',
      },
    };
  },
  (error) => Promise.reject(error),
);

addResponseInterceptors(
  (response) => Promise.resolve(response),
  (error) => {
    // the API rejected our token
    if (error.response?.status === 401) {
      logout();
    }
    return Promise.reject(error);
  },
);

/**
 * @class AuthService
 * @description All Auth related services
 */
class AuthService {
  /**
   * @method login
   * @param username
   * @param password
   * @return login to keycloak with provided user name and password
   */
  public static async login(username?: string, password?: string): Promise<SessionModel> {
    const requestConfig = new APIParamsBuilderHelper.ParamsBuilder(AuthConstants.LOGIN_API, HttpMethods.POST)
      .addRequestBody({ username, password })
      .build().getConfig;

    const result = await APIHelper.sendRequest(requestConfig).catch((err: string) => {
      throw err;
    });
    const session: SessionModel = result?.data;
    const { email, firstName, lastName } = session;
    if (email) {
      BrowserHelper.setSessionStorageItem(AuthConstants.USER_DATA_EMAIL, email);
    }
    if (firstName) {
      BrowserHelper.setSessionStorageItem(AuthConstants.USER_DATA_FIRSTNAME, firstName);
    }
    if (lastName) {
      BrowserHelper.setSessionStorageItem(AuthConstants.USER_DATA_LASTNAME, lastName);
    }
    return session;
  }

  public static checkUserLogin(): boolean {
    return !!BrowserHelper.getSessionStorageItem(AuthConstants.USER_DATA_EMAIL);
  }
}

export default AuthService;
